// NOTE: THIS IS A SAMPLE GUIDE TEMPLATE FOR THE ESTATE ESSENTIALS MODULE. IT IS NOT THE REAL TEMPLATE.

export const SAMPLE_GUIDE_TEMPLATE = `# Overview

This guide walks you through the process of canceling {{deceased_first_name}}'s state-issued identification. Each state has its own requirements, so the steps below have been **tailored to {{state}}** based on the information you provided.

Before you begin, make sure you have access to the required documents listed in the next section. If you are missing anything, reach out to our support team and we can help you locate alternatives.

## Why this matters

Canceling a state ID or driver's license helps prevent ***identity theft*** and ensures that {{deceased_first_name}}'s records are properly updated with the state. Leaving an active ID on file can create complications during probate and may delay other parts of the estate settlement process.

![Guided setup illustration](/src/shared/assets/illustrations/guided-setup.svg)

# Required Documents

You will need the following documents to complete this process. Items marked as required **must** be originals or certified copies.

## Essential documents

1. **Certified death certificate** (original or certified copy)
2. **{{deceased_first_name}}'s driver's license or state ID** (if available)
3. Letters of administration or letters testamentary
4. Your own valid photo ID

## Supporting documents

If you do not have the original ID, you may also need:

- A completed _affidavit of lost document_ (available at your local DMV)
- Proof of your authority as executor or administrator
- A notarized copy of the will, if applicable

> **Tip:** Call your local DMV office ahead of time to confirm exactly which documents they require. Requirements can vary by county within {{state}}.

# Step-by-Step Instructions

Follow the steps below in order. Some steps may not apply depending on your specific situation in {{state}}.

### Step 1: Gather your documents

Collect all items listed in the _Required Documents_ section above. Make photocopies of everything for your records before submitting originals.

### Step 2: Contact the DMV

You can reach the {{state}} DMV by:

- Visiting their website and searching for "deceased" or "cancel ID"
- Calling the main office line during business hours
- Visiting a local branch ***in person*** (recommended for faster processing)

### Step 3: Submit the cancellation request

At the DMV, you will need to:

1. Present the certified death certificate
2. Present your own valid ID
3. Present proof of executor or administrator status
4. Complete any required forms provided by the office
5. Request a **written confirmation** of the cancellation

### Step 4: Follow up

After submitting your request, keep the written confirmation in a safe place. You may need it for:

- Closing financial accounts
- Updating insurance policies
- Completing probate filings

# Checklist

Use this checklist to track your progress through the cancellation process.

- [ ] Obtained certified death certificate
- [ ] Located {{deceased_first_name}}'s state ID or filed lost document affidavit
- [ ] Gathered letters testamentary or letters of administration
- [ ] Contacted the {{state}} DMV to confirm requirements
- [ ] Visited DMV or submitted cancellation online
- [ ] Received written confirmation of cancellation
- [ ] Filed confirmation with estate records

# Additional Resources

Below are some resources that may be helpful as you work through this process.

## Online resources

- The [{{state}} DMV website](https://www.dmv.org) often has a dedicated section for reporting a deceased individual
- The [Social Security Administration](https://www.ssa.gov) should also be notified separately (see our *Social Security Benefits* guide)
- Your county clerk's office can provide certified copies of the death certificate if you need more

## When to seek professional help

If you encounter any of the following, consider consulting an attorney:

1. The DMV requires documents you cannot obtain
2. There is a dispute about who has authority to act on behalf of the estate
3. {{deceased_first_name}}'s identity has already been compromised

---

*This guide was generated based on information you provided and general {{state}} requirements. Always verify details directly with your local DMV office.*`;
